import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowForward as ArrowForwardIcon, InsertEmoticonOutlined } from '@mui/icons-material';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { Controller, ControllerRenderProps, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { getOrganisationProfile, updateOrganisationProfile } from '../../services';

interface OrganisationFormData {
  name: string;
  legalName: string;
  abn: string;
  contactNumber: string;
  website: string;
  billingEmail: string;
  billingAddress: string;
}

// Validation schema - same as Organisation.tsx
const schema = yup.object({
  name: yup.string().required('Organisation name is required'),
  legalName: yup.string().default(''),
  abn: yup
    .string()
    .default('')
    .test('abn-format', 'ABN must be 11 digits', function (value: string | undefined) {
      if (!value || !value.trim()) return true; // Optional field
      const cleanABN = value.replace(/\s/g, '');
      return /^\d{11}$/.test(cleanABN);
    }),
  contactNumber: yup.string().default(''),
  website: yup
    .string()
    .default('')
    .test(
      'url-format',
      'Website must start with http:// or https://',
      function (value: string | undefined) {
        if (!value || !value.trim()) return true; // Optional field
        return /^https?:\/\/.+/.test(value);
      }
    ),
  billingEmail: yup
    .string()
    .required('Billing email is required')
    .email('Please enter a valid email'),
  billingAddress: yup.string().default(''),
});

const OrganisationOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { organisationId, refreshUserOrganisationState } = useAuth();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<OrganisationFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      legalName: '',
      abn: '',
      contactNumber: '',
      website: '',
      billingEmail: '',
      billingAddress: '',
    },
  });

  useEffect(() => {
    fetchOrganisationData();
  }, []);

  const fetchOrganisationData = async () => {
    if (!organisationId) {
      enqueueSnackbar('No organisation ID found', { variant: 'error' });
      setFetchingData(false);
      return;
    }

    try {
      const organisation = await getOrganisationProfile(organisationId);

      if (organisation) {
        // Set form values with formatted data - same as Organisation.tsx
        setValue('name', organisation.name || '');
        setValue('legalName', organisation.legalName || '');
        setValue('abn', organisation.abn ? formatABN(organisation.abn) : '');
        setValue('contactNumber', organisation.contactNumber || '');
        setValue('website', organisation.website || '');
        setValue('billingEmail', organisation.billingEmail || '');
        setValue('billingAddress', organisation.billingAddress || '');

        // Reset form with initial values to clear dirty state
        reset({
          name: organisation.name || '',
          legalName: organisation.legalName || '',
          abn: organisation.abn ? formatABN(organisation.abn) : '',
          contactNumber: organisation.contactNumber || '',
          website: organisation.website || '',
          billingEmail: organisation.billingEmail || '',
          billingAddress: organisation.billingAddress || '',
        });
      }
    } catch (error) {
      // Don't show error for fetch failures on onboarding - just proceed with empty form
      console.error('Organisation not found or error fetching - proceeding with empty form');
    } finally {
      setFetchingData(false);
    }
  };

  const onSubmit = async (formData: OrganisationFormData) => {
    if (!organisationId) {
      enqueueSnackbar('No organisation ID found', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      // Clean up the ABN by removing spaces for storage and prepare update payload - same as Organisation.tsx
      const updatePayload = {
        ...formData,
        abn: formData.abn.replace(/\s/g, ''),
        website: formData.website || undefined,
      };

      await updateOrganisationProfile(organisationId, updatePayload);
      await refreshUserOrganisationState();

      navigate('/onboarding/project');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to save organisation details';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Error saving organisation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/project');
  };

  // Same formatABN function as Organisation.tsx
  const formatABN = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX XXX XXX XXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  if (fetchingData) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box mb={4} textAlign="center">
        <InsertEmoticonOutlined sx={{ fontSize: 60, mb: 1 }} color="secondary" />
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Welcome to Avanzu
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Let's start by giving your organisation a name and confirming your billing email.
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          {/* Company Information Section */}
          <Stack spacing={4} pb={6}>
            <Controller
              name="name"
              control={control}
              render={({
                field,
              }: {
                field: ControllerRenderProps<OrganisationFormData, 'name'>;
              }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Organisation Nick-Name"
                  placeholder="Acme"
                  error={!!errors.name}
                  helperText={
                    errors.name?.message || 'Give your company a nickname, only visible to you'
                  }
                />
              )}
            />
            <Stack spacing={2}>
              <Controller
                name="billingEmail"
                control={control}
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<OrganisationFormData, 'billingEmail'>;
                }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Billing Email"
                    placeholder="accounts@example.com.au"
                    type="email"
                    error={!!errors.billingEmail}
                    helperText={
                      errors.billingEmail?.message ||
                      "Where we'll send invoices and billing notifications"
                    }
                  />
                )}
              />
            </Stack>
          </Stack>

          {/* Action Buttons */}
          <Box
            sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mt: 6, gap: 2 }}
          >
            <Button
              variant="text"
              color="inherit"
              onClick={handleSkip}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              Skip for now
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {loading ? 'Saving...' : 'Continue'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

export default OrganisationOnboarding;
